import { Component, OnDestroy } from '@angular/core'
import { Store } from '@ngrx/store'
import {
    selectHierarchicalSelectionNodeItem,
    selectSavedHierarchicalSelectionNodeItems,
    selectHierarchicalSelectionIsRoot,
    defaultHierarchicalSelectionNodeItem,
    selectDisplayedSelectionsOnScatterPlot,
    selectSelectionState,
    selectSavedUnsupervisedSelectionNodeItems,
} from '../../../state/selection/selection.selectors'
import { selectionActions } from '../../../state/selection/selection.actions'
import { selectSavedAndCurrentHierarchicalSelectionsAsLayers } from '../../../state/selection/selection.selectors'
import { Dialog } from '@angular/cdk/dialog'
import { SaveSelectionAsDialogComponent } from '../../save-selection-as-dialog/save-selection-as-dialog.component'
import { Subject, takeUntil, take, map, combineLatest } from 'rxjs'
import { GenericConfirmationDialogComponent } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component'
import { GenericConfirmationDialogModel } from '../../../shared/components/generic-confirmation-dialog/generic-confirmation-dialog.model'
import { selectIsSharedCase } from '../../../state/case/case.selectors'
import { SelectionTreeItem } from '../../../state/selection/selection.state'
import { ToasterService } from '../../../shared/services/toaster.service'
import { topLevelSelectionName } from 'src/app/state/selection/selection.reducer'
import { SelectionType } from 'src/app/state/selection/selection.state'

@Component({
    selector: 'app-saved-hierarchical-selection-nodes',
    templateUrl: './saved-hierarchical-selection-nodes.component.html',
    styleUrls: ['./saved-hierarchical-selection-nodes.component.scss'],
})
export class SavedHierarchicalSelectionNodesComponent implements OnDestroy {
    readonly currentHierarchicalSelectionNodeItem$ = this.store.select(
        selectHierarchicalSelectionNodeItem
    )
    readonly savedHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedHierarchicalSelectionNodeItems
    )
    readonly savedUnsupervisedSelectionNodeItems$ = this.store.select(
        selectSavedUnsupervisedSelectionNodeItems
    )

    readonly savedSelectionNodeItems$ = combineLatest({
        selectionState: this.store.select(selectSelectionState),
        hierarchicalSelectionNodeItems: this.store.select(
            selectSavedHierarchicalSelectionNodeItems
        ),
        unsupervisedSelectionNodeItems: this.store.select(
            selectSavedUnsupervisedSelectionNodeItems
        ),
    }).pipe(
        map(
            ({
                selectionState,
                hierarchicalSelectionNodeItems,
                unsupervisedSelectionNodeItems,
            }) => {
                if (selectionState.type === SelectionType.Hierarchical) {
                    return hierarchicalSelectionNodeItems
                } else {
                    return unsupervisedSelectionNodeItems
                }
            }
        )
    )

    readonly currentHierarchicalSelectionIsRoot$ = this.store.select(
        selectHierarchicalSelectionIsRoot
    )
    readonly allHierarchicalSelectionNodeItems$ = this.store.select(
        selectSavedAndCurrentHierarchicalSelectionsAsLayers(this.store)
    )
    readonly isSharedCase$ = this.store.select(selectIsSharedCase)
    readonly displayedSelections$ = this.store.select(selectDisplayedSelectionsOnScatterPlot)

    readonly defaultHierarchicalSelectionNodeItem: SelectionTreeItem =
        defaultHierarchicalSelectionNodeItem
    readonly selectionState$ = this.store.select(selectSelectionState)
    isUnsupervisedSelection = false

    private readonly destroy$ = new Subject<void>()

    constructor(
        private readonly store: Store,
        private readonly dialog: Dialog,
        private readonly toasterService: ToasterService
    ) {
        this.selectionState$.pipe().subscribe(selectionState => {
            if (selectionState.type === SelectionType.Hierarchical) {
                this.isUnsupervisedSelection = false
            } else {
                this.isUnsupervisedSelection = true
            }
        })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    addHierarchicalSelectionNode(): void {
        let savedSelections: string[] = []
        if (this.isUnsupervisedSelection) {
            this.savedUnsupervisedSelectionNodeItems$.pipe(take(1)).subscribe(savedItems => {
                savedItems.map(s => s.name && savedSelections.push(s.name))
            })
        } else {
            this.savedHierarchicalSelectionNodeItems$.pipe(take(1)).subscribe(savedItems => {
                savedItems.map(s => s.name && savedSelections.push(s.name))
            })
        }

        const dialogRef = this.dialog.open<string>(SaveSelectionAsDialogComponent, {
            autoFocus: 'first-tabbable',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(name => {
            if (!name) return
            if (savedSelections.includes(name || '')) {
                this.toasterService.error('Selection name already exists', 'Error saving selection')
                return
            }
            if (this.isUnsupervisedSelection) {
                this.store.dispatch(selectionActions.saveNewAutoCellTypeDetectionNode({ name }))
            } else {
                this.store.dispatch(selectionActions.saveNewHierarchicalSelectionNode({ name }))
            }
        })
    }

    setSelection(selectionTreeItem: SelectionTreeItem): void {
        this.store.dispatch(
            selectionActions.setHierarchicalSelectionNode({ name: selectionTreeItem.name })
        )
    }

    removeHierarchicalSelectionNode(selectionTreeItem: SelectionTreeItem): void {
        if (!selectionTreeItem.name) return
        const data: GenericConfirmationDialogModel = {
            title: `Are you sure you want to delete this ${
                this.isUnsupervisedSelection ? 'unsupervised' : 'hierarchical'
            } node?`,
            item: selectionTreeItem.name,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return
            if (this.isUnsupervisedSelection) {
                this.store.dispatch(selectionActions.deleteAutoCellTypeDetectionNode())
            } else {
                this.store.dispatch(selectionActions.deleteHierarchicalSelectionNode())
            }
        })
    }

    removeHierarchicalSelectionNodeRecursively(selectionTreeItem: SelectionTreeItem): void {
        if (!selectionTreeItem.name) return
        const data: GenericConfirmationDialogModel = {
            title: `Are you sure you want to delete this ${
                this.isUnsupervisedSelection ? 'unsupervised' : 'hierarchical'
            } node?`,
            item: `${selectionTreeItem.name} (Recursively)`,
            confirmButtonLabel: 'Delete',
            cancelButtonLabel: 'Cancel',
        }
        const dialogRef = this.dialog.open<string>(GenericConfirmationDialogComponent, {
            data,
            autoFocus: 'dialog',
        })
        dialogRef.closed.pipe(takeUntil(this.destroy$)).subscribe(confirmed => {
            if (!confirmed) return

            if (this.isUnsupervisedSelection) {
                this.store.dispatch(
                    selectionActions.deleteAutoCellTypeDetectionNodeRecursively({
                        groupName: selectionTreeItem.groupName!,
                        name: selectionTreeItem.name!,
                    })
                )
            } else {
                this.store.dispatch(
                    selectionActions.deleteHierarchicalSelectionNodeRecursively({
                        groupName: selectionTreeItem.groupName!,
                        name: selectionTreeItem.name!,
                    })
                )
            }
        })
    }

    compareSelectionTreeItems(
        selectionTreeItem1: SelectionTreeItem | null,
        selectionTreeItem2: SelectionTreeItem | null
    ): boolean {
        if (!selectionTreeItem1 || !selectionTreeItem2) {
            return selectionTreeItem1 === selectionTreeItem2
        }
        return selectionTreeItem1.name === selectionTreeItem2.name
    }

    isTopLevelSelection(selectionTreeItem: SelectionTreeItem): boolean {
        return selectionTreeItem.name === topLevelSelectionName
    }
}
